import { clamp } from '../core/math';
import { CELL, GRID_H, GRID_W, Grid, LOGICAL_H, LOGICAL_W, PKind, PState, T, TICK, idx } from './types';
import type { World } from './world';

export type HeliMode = 'bucket' | 'seats';
export type HeliAction = null | 'scoop' | 'load' | 'unload' | 'refuel' | 'swap';

export interface HeliInput {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  /** Held: context action (scoop / land and load / unload and refuel). */
  interact: boolean;
  /** Pressed: release the water. */
  drop: boolean;
  /** Pressed: swap bucket for seats, only on the base pad. */
  swap: boolean;
}

export interface Heli {
  /** Logical pixels. */
  x: number;
  y: number;
  vx: number;
  vy: number;
  mode: HeliMode;
  /** 0..1 of tank. */
  water: number;
  aboard: number[];
  /** 0..1 of fuel. */
  fuel: number;
  landed: boolean;
  action: HeliAction;
  progress: number;
  /** Seconds of forced grounding after running the tank dry. */
  grounded: number;
  /** Seconds left of a bucket/seats change on the base pad. */
  swapping: number;
  facing: number;
  rotor: number;
  /** Ticks since the last drop, for the HUD flash. */
  sinceDrop: number;
}

const ACCEL = 70;
const DRAG = 1.75;
const MAX_SPEED = 112;
const LAND_SPEED = 16;
const SCOOP_SPEED = 34;
/**
 * Wind pushes the helicopter a little. It must never push hard enough to steer
 * it. A real pilot corrects for a steady wind without thinking about it, and
 * having to work the controls constantly just to stay in one place was
 * unpleasant rather than difficult.
 */
const WIND_PUSH = 9;
/**
 * How far sideways the wind carries the falling water. Tonnes of water do not
 * travel far across a mountainside. The drift is small enough that the player
 * can aim off for it, so the landing point is predictable and not random.
 */
export const DROP_DRIFT = 15;
/**
 * How long the water is in the air. Rounded to whole ticks so that the ring
 * drawn on the ground and the landing point the simulation works out cannot
 * differ by a fraction of a tick. The ring has to be exactly where the water
 * lands.
 */
export const DROP_FALL = Math.ceil(0.85 / TICK) * TICK;

export function makeHeli(baseX: number, baseY: number): Heli {
  return {
    x: baseX,
    y: baseY,
    vx: 0,
    vy: 0,
    mode: 'bucket',
    water: 0,
    aboard: [],
    fuel: 1,
    landed: true,
    action: null,
    progress: 0,
    grounded: 0,
    swapping: 0,
    facing: 1,
    rotor: 0,
    sinceDrop: 99,
  };
}

const speedOf = (h: Heli): number => Math.hypot(h.vx, h.vy);

const cellAt = (x: number, y: number): number =>
  idx(clamp(Math.floor(x / CELL), 0, GRID_W - 1), clamp(Math.floor(y / CELL), 0, GRID_H - 1));

/** Terrain cell under the helicopter itself. This is the cell it lands on. */
export function heliCell(h: Heli): number {
  return cellAt(h.x, h.y);
}

/** How far the bucket hangs below the fuselage (the helicopter's body), in logical pixels. */
export const BUCKET_DROP = 16;

/**
 * Where the bucket actually is. The renderer draws it here and the scoop test
 * uses it, so the bucket the player sees dip into the water is the one that
 * fills.
 */
export function bucketAnchor(h: Heli): { x: number; y: number; swing: number } {
  const swing = clamp(-h.vx * 0.1, -7, 7);
  return { x: h.x + swing, y: h.y + BUCKET_DROP, swing };
}

/** Terrain cell under the bucket. */
export function bucketCell(h: Heli): number {
  const a = bucketAnchor(h);
  return cellAt(a.x, a.y);
}

/**
 * Can the bucket draw water where it is? Open water within one cell counts. A
 * real bucket is wider than a cell and a hovering helicopter is never perfectly
 * still, so demanding an exact cell made the shallow edges of the reservoir
 * unusable. This one function answers the question everywhere: the scoop, the
 * on-screen prompt and the ripple under the bucket all call it.
 */
export function bucketInWater(grid: Grid, h: Heli): boolean {
  const a = bucketAnchor(h);
  const bx = clamp(Math.floor(a.x / CELL), 0, GRID_W - 1);
  const by = clamp(Math.floor(a.y / CELL), 0, GRID_H - 1);
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const x = bx + dx;
      const y = by + dy;
      if (x < 0 || y < 0 || x >= GRID_W || y >= GRID_H) continue;
      if (grid.terrain[idx(x, y)] === T.Water) return true;
    }
  }
  return false;
}

export function stepHeli(w: World, input: HeliInput, dt: number): void {
  const h = w.heli;
  const { grid, diff } = w;
  h.rotor += dt * (h.landed ? 9 : 26);
  h.sinceDrop += dt;

  if (h.grounded > 0) {
    h.grounded -= dt;
    h.vx = 0;
    h.vy = 0;
    h.landed = true;
    h.action = null;
    if (h.grounded <= 0) {
      h.fuel = 1;
      w.say('Refuelled by the bowser, the fuel truck. You can fly again.', 'good');
    }
    return;
  }

  const terrain = grid.terrain[heliCell(h)];
  // The bucket is what goes in the water; the aircraft is what lands on a pad.
  const overWater = bucketInWater(grid, h);
  const overPad = terrain === T.Helipad;
  const overBase = terrain === T.Base;
  const speed = speedOf(h);

  // --- Load swap ------------------------------------------------------------
  // The timer is stored on the helicopter and counted down, rather than worked
  // out from the keys each tick: once the ground crew start, they finish, and
  // nothing the player does in the meantime can cancel it without saying so.
  if (h.swapping > 0) {
    h.swapping -= dt;
    h.vx = 0;
    h.vy = 0;
    h.landed = true;
    h.action = 'swap';
    if (h.swapping <= 0) {
      h.swapping = 0;
      h.mode = h.mode === 'bucket' ? 'seats' : 'bucket';
      h.water = 0;
      h.action = null;
      w.flags.fullTankSaid = false;
      w.say(
        h.mode === 'seats'
          ? `Bucket off, ${diff.seats} seats in. You cannot drop water now.`
          : 'Seats out, bucket on. No passengers until you swap back.',
        'info',
      );
    }
    return;
  }

  if (input.swap) {
    if (!overBase) w.say('The load swap needs the base pad down in the valley.', 'warn');
    else if (speed >= LAND_SPEED) w.say('Too fast for the ground crew. Land on the pad first.', 'warn');
    else if (h.aboard.length > 0) w.say('Get your passengers off before swapping the load.', 'warn');
    else {
      h.swapping = diff.swapTime;
      w.say(
        `Ground crew fitting ${h.mode === 'bucket' ? `${diff.seats} seats` : 'the bucket'}. Takes ${diff.swapTime}s.`,
        'info',
      );
      return;
    }
  }

  // --- Context action ------------------------------------------------------
  let action: HeliAction = null;
  if (input.interact) {
    if (overWater && h.mode === 'bucket' && h.water < 1 && speed < SCOOP_SPEED) action = 'scoop';
    else if (overPad && speed < LAND_SPEED) action = h.mode === 'seats' ? 'load' : null;
    else if (overBase && speed < LAND_SPEED) action = h.aboard.length > 0 ? 'unload' : h.fuel < 0.999 ? 'refuel' : null;
  }
  if (action !== h.action) h.progress = 0;
  h.action = action;

  h.landed = (overPad || overBase) && speed < LAND_SPEED && input.interact;

  switch (action) {
    case 'scoop': {
      h.water = Math.min(1, h.water + dt / diff.scoopTime);
      const a = bucketAnchor(h);
      if (w.rng.chance(dt * 26)) {
        w.spawnParticle(a.x + w.rng.range(-5, 5), a.y + w.rng.range(-1, 3), w.rng.range(-14, 14), w.rng.range(-34, -10), 0.5, PKind.Splash);
      }
      if (h.water >= 1 && !w.flags.fullTankSaid) {
        w.flags.fullTankSaid = true;
        w.say('Bucket full. Fly to the fire and drop it.', 'info');
      }
      break;
    }
    case 'load': {
      h.progress += dt;
      while (h.progress >= 0.4 && h.aboard.length < diff.seats) {
        h.progress -= 0.4;
        const p = w.people.find((q) => q.state === PState.Waiting);
        if (!p) break;
        p.state = PState.Aboard;
        h.aboard.push(p.id);
        if (h.aboard.length === diff.seats) w.say(`Full load, ${diff.seats} aboard. Fly them down to base.`, 'info');
      }
      break;
    }
    case 'unload': {
      h.progress += dt;
      while (h.progress >= 0.3 && h.aboard.length > 0) {
        h.progress -= 0.3;
        const id = h.aboard.shift()!;
        const p = w.people[id];
        p.state = PState.Rescued;
        for (let k = 0; k < 3; k++)
          w.spawnParticle(h.x + w.rng.range(-8, 8), h.y + w.rng.range(-2, 6), w.rng.range(-8, 8), w.rng.range(-24, -8), 1.1, PKind.Cheer);
      }
      break;
    }
    case 'refuel': {
      h.fuel = Math.min(1, h.fuel + diff.refuelRate * dt);
      break;
    }
    default:
      break;
  }

  // --- Flight --------------------------------------------------------------
  if (!h.landed) {
    let ax = 0;
    let ay = 0;
    if (input.left) ax -= 1;
    if (input.right) ax += 1;
    if (input.up) ay -= 1;
    if (input.down) ay += 1;
    const m = Math.hypot(ax, ay);
    if (m > 0) {
      ax /= m;
      ay /= m;
      h.facing = ax !== 0 ? Math.sign(ax) : h.facing;
    }
    // Heavier with a full bucket or a full cabin.
    const load = 1 + h.water * 0.35 + (h.aboard.length / Math.max(1, diff.seats)) * 0.25;
    h.vx += (ax * ACCEL) / load * dt + w.wind.ux * w.wind.speed * WIND_PUSH * dt;
    h.vy += (ay * ACCEL) / load * dt + w.wind.uy * w.wind.speed * WIND_PUSH * dt;
    h.vx -= h.vx * DRAG * dt;
    h.vy -= h.vy * DRAG * dt;
    const sp = speedOf(h);
    if (sp > MAX_SPEED) {
      h.vx = (h.vx / sp) * MAX_SPEED;
      h.vy = (h.vy / sp) * MAX_SPEED;
    }
    h.x = clamp(h.x + h.vx * dt, 6, LOGICAL_W - 6);
    h.y = clamp(h.y + h.vy * dt, 6, LOGICAL_H - 6);

    const burn = diff.fuelBurn * dt * (0.55 + (sp / MAX_SPEED) * 0.7);
    h.fuel = Math.max(0, h.fuel - burn);
    if (h.fuel <= 0) {
      h.grounded = 45;
      h.water = 0;
      w.say('OUT OF FUEL. Forced landing. Forty-five seconds on the ground.', 'bad');
    } else if (h.fuel < 0.2 && !w.flags.lowFuelSaid) {
      w.flags.lowFuelSaid = true;
      w.say('Fuel below twenty percent. Get back to base.', 'warn');
    } else if (h.fuel > 0.5) {
      w.flags.lowFuelSaid = false;
    }
  } else {
    h.vx = 0;
    h.vy = 0;
  }

  // --- Drop ----------------------------------------------------------------
  if (input.drop && h.mode === 'bucket' && h.water > 0.05) {
    const amount = h.water;
    w.drops.push({
      x: h.x,
      y: h.y + 4,
      vx: h.vx * 0.35 + w.wind.ux * w.wind.speed * DROP_DRIFT,
      vy: h.vy * 0.35 + w.wind.uy * w.wind.speed * DROP_DRIFT,
      t: 0,
      fall: DROP_FALL,
      amount: 0.95 * amount,
      radius: diff.dropRadius * Math.max(0.55, Math.sqrt(amount)),
    });
    h.water = 0;
    h.sinceDrop = 0;
    w.flags.fullTankSaid = false;
  }
}

/** What the on-screen prompt should say, given where the aircraft is. */
export function heliPrompt(w: World): string | null {
  const h = w.heli;
  if (h.grounded > 0) return `GROUNDED · ${Math.ceil(h.grounded)}s`;
  if (h.swapping > 0) return `CHANGING LOAD · ${h.swapping.toFixed(1)}s`;
  const t = w.grid.terrain[heliCell(h)];
  const slow = speedOf(h) < LAND_SPEED;
  if (h.mode === 'bucket' && bucketInWater(w.grid, h)) {
    if (h.water < 1) return 'HOLD E TO SCOOP';
    return 'BUCKET FULL. SPACE TO DROP';
  }
  if (t === T.Helipad && slow) {
    if (h.mode === 'seats') {
      const waiting = w.stats.waiting;
      if (h.aboard.length >= w.diff.seats) return 'FULL. RETURN TO BASE';
      return waiting > 0 ? `HOLD E TO LOAD (${waiting} waiting)` : 'NOBODY HERE YET';
    }
    return 'BUCKET FITTED, NO SEATS';
  }
  if (t === T.Base) {
    const to = h.mode === 'bucket' ? `B TO FIT ${w.diff.seats} SEATS` : 'B TO FIT BUCKET';
    if (!slow) return 'SLOW DOWN OVER THE PAD';
    if (h.aboard.length > 0) return 'HOLD E TO UNLOAD';
    if (h.fuel < 0.999) return `HOLD E TO REFUEL   ${to}`;
    return to;
  }
  if (h.mode === 'bucket' && h.water > 0.05) return 'SPACE TO DROP';
  return null;
}


